import React, { useState, useEffect, useCallback } from 'react';
import { getProducts, updateProduct, deleteProduct } from '../api/client';

export default function ProductMaster({ role }) {
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState({});
  const [saving, setSaving] = useState({});
  const [edits, setEdits] = useState({});
  const [confirmDel, setConfirmDel] = useState(null);

  const load = useCallback(async () => {
    const res = await getProducts();
    setProducts(res.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = id => setOpen(o => ({ ...o, [id]: !o[id] }));

  // Update local edit state (no API call, no close)
  const setEdit = (id, field, val) =>
    setEdits(e => ({ ...e, [id]: { ...(e[id] || {}), [field]: val } }));

  // Immediate boolean toggle — save to API, refresh, keep panel open
  const saveToggle = async (id, data) => {
    await updateProduct(id, data);
    setProducts(ps => ps.map(p => p.id === id ? { ...p, ...data } : p));
  };

  // Save all local edits, then close panel
  const saveProduct = async id => {
    setSaving(s => ({ ...s, [id]: true }));
    try {
      const data = edits[id] || {};
      if (Object.keys(data).length > 0) {
        const parsed = {};
        for (const [k, v] of Object.entries(data)) {
          parsed[k] = typeof v === 'string' && v !== '' && !isNaN(v) ? parseFloat(v) : v;
        }
        await updateProduct(id, parsed);
      }
      setEdits(e => { const n = { ...e }; delete n[id]; return n; });
      await load();
      setOpen(o => ({ ...o, [id]: false }));
    } finally {
      setSaving(s => ({ ...s, [id]: false }));
    }
  };

  const doDelete = async () => {
    if (!confirmDel) return;
    await deleteProduct(confirmDel.id);
    setConfirmDel(null);
    await load();
  };

  // Get field value: local edit if present, else product value
  const v = (p, field) =>
    edits[p.id]?.[field] !== undefined ? edits[p.id][field] : (p[field] ?? '');
  const bv = (p, field) =>
    edits[p.id]?.[field] !== undefined ? edits[p.id][field] : p[field];

  const highlight = text => {
    if (!search) return text;
    const re = new RegExp(`(${search.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    return text.split(re).map((part, i) =>
      re.test(part) ? <mark key={i} style={{ background: 'rgba(245,158,11,.3)', borderRadius: 2 }}>{part}</mark> : part
    );
  };

  const filtered = products
    .filter(p => !search || p.name.toLowerCase().includes(search.toLowerCase()) || p.asin.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => {
      if (!search) return (b.dash_visible ? 1 : 0) - (a.dash_visible ? 1 : 0);
      const am = a.name.toLowerCase().includes(search.toLowerCase());
      const bm = b.name.toLowerCase().includes(search.toLowerCase());
      return am && !bm ? -1 : !am && bm ? 1 : 0;
    });

  const s = {
    card: { background: 'var(--surface,#fff)', border: '1px solid var(--border,#e2e0d8)', borderRadius: 10, overflow: 'hidden', marginBottom: 2 },
    hdr: { display: 'flex', alignItems: 'center', gap: 12, padding: '13px 18px', cursor: 'pointer', userSelect: 'none' },
    body: { borderTop: '1px solid var(--border,#e2e0d8)', padding: '20px 18px', background: 'var(--surface2,#f0efe9)' },
    label: { display: 'block', fontSize: 10, color: 'var(--text3,#9b9890)', textTransform: 'uppercase', letterSpacing: .4, marginBottom: 4 },
    inp: { width: '100%', padding: '7px 10px', border: '1px solid var(--border,#e2e0d8)', borderRadius: 6, background: 'var(--surface,#fff)', color: 'var(--text,#1a1917)', fontSize: 12, fontFamily: 'DM Mono,monospace', outline: 'none' },
    stitle: { fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: .6, color: 'var(--text3,#9b9890)', marginBottom: 10 },
    brow: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 },
    mb: { marginBottom: 10 },
  };

  const Tog = ({ on, onChange }) => (
    <div onClick={e => { e.stopPropagation(); onChange(!on); }}
      style={{ width: 28, height: 16, background: on ? '#22c55e' : '#cccac0', borderRadius: 8, position: 'relative', cursor: 'pointer', flexShrink: 0 }}>
      <div style={{ position: 'absolute', top: 2, left: on ? 14 : 2, width: 12, height: 12, background: 'white', borderRadius: '50%', transition: 'left .2s' }} />
    </div>
  );

  return (
    <div>
      {confirmDel && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.52)', zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: 'var(--surface,#fff)', borderRadius: 12, padding: 28, width: 380, boxShadow: '0 20px 60px rgba(0,0,0,.22)' }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text,#1a1917)', marginBottom: 8 }}>Remove product?</div>
            <div style={{ fontSize: 13, color: 'var(--text2,#6b6960)', marginBottom: 24, lineHeight: 1.5 }}>
              <strong>{confirmDel.name}</strong> will be permanently removed from tracking. This cannot be undone.
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setConfirmDel(null)}
                style={{ flex: 1, padding: '9px 0', border: '1px solid var(--border,#e2e0d8)', borderRadius: 7, fontSize: 13, background: 'none', cursor: 'pointer', color: 'var(--text2,#6b6960)' }}>
                Cancel
              </button>
              <button onClick={doDelete}
                style={{ flex: 1, padding: '9px 0', background: '#dc2626', color: '#fff', border: 'none', borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                Yes, remove
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <h2 style={{ fontSize: 18, fontWeight: 600 }}>Product master</h2>
        <span style={{ fontSize: 12, color: 'var(--text3,#9b9890)' }}>{filtered.length} of {products.length} products</span>
      </div>
      <div style={{ marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'var(--surface,#fff)', border: '1px solid var(--border,#e2e0d8)', borderRadius: 6, padding: '7px 12px', maxWidth: 360 }}>
          <span style={{ color: 'var(--text3,#9b9890)' }}>⌕</span>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search products… (live filter)"
            style={{ border: 'none', background: 'none', outline: 'none', fontSize: 13, color: 'var(--text,#1a1917)', width: '100%' }} />
        </div>
      </div>

      {filtered.map(p => (
        <div key={p.id} style={s.card}>
          <div style={s.hdr} onClick={() => toggle(p.id)}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text,#1a1917)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{highlight(p.name)}</div>
              <div style={{ fontSize: 10, color: 'var(--text3,#9b9890)', fontFamily: 'DM Mono,monospace', marginTop: 2 }}>{p.asin}</div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
              <span style={{ padding: '2px 7px', borderRadius: 20, fontSize: 10, fontWeight: 600, background: p.in_stock ? '#d1fae5' : '#fef3c7', color: p.in_stock ? '#065f46' : '#92400e' }}>
                {p.in_stock ? 'In stock' : 'OOS'}
              </span>
              <span style={{ fontSize: 11, color: 'var(--text3,#9b9890)' }}>Show in dashboard</span>
              <Tog on={p.dash_visible} onChange={val => saveToggle(p.id, { dash_visible: val })} />
              <span style={{ color: 'var(--text3,#9b9890)', fontSize: 12, transform: open[p.id] ? 'rotate(90deg)' : 'none', display: 'inline-block', transition: 'transform .2s' }}>▶</span>
            </div>
          </div>

          {open[p.id] && (
            <div style={s.body}>
              {role === 'team' ? (
                <div style={{ maxWidth: 360 }}>
                  <div style={s.stitle}>Product</div>
                  <div style={s.mb}><label style={s.label}>Name</label>
                    <input value={v(p, 'name')} onChange={e => setEdit(p.id, 'name', e.target.value)} style={s.inp} /></div>
                  <div style={s.mb}><label style={s.label}>ASIN</label>
                    <input value={p.asin} disabled style={{ ...s.inp, opacity: .5, cursor: 'not-allowed' }} /></div>
                  <div style={s.mb}><label style={s.label}>Colour</label>
                    <input value={v(p, 'colour')} onChange={e => setEdit(p.id, 'colour', e.target.value)} style={s.inp} /></div>
                  <div style={s.mb}><label style={s.label}>Target Price (₹)</label>
                    <input type="number" value={v(p, 'domestic_srp')} onChange={e => setEdit(p.id, 'domestic_srp', e.target.value)} style={s.inp} /></div>
                  <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--border,#e2e0d8)' }}>
                    <button onClick={() => saveProduct(p.id)}
                      style={{ padding: '8px 20px', background: '#1a1917', color: '#fff', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', opacity: saving[p.id] ? .7 : 1 }}>
                      {saving[p.id] ? 'Saving…' : 'Save changes'}
                    </button>
                  </div>
                </div>
              ) : (<>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 20 }}>
                <div>
                  <div style={s.stitle}>Product</div>
                  <div style={s.mb}><label style={s.label}>Name</label>
                    <input value={v(p, 'name')} onChange={e => setEdit(p.id, 'name', e.target.value)} style={s.inp} /></div>
                  <div style={s.mb}><label style={s.label}>ASIN</label>
                    <input value={v(p, 'asin')} onChange={e => setEdit(p.id, 'asin', e.target.value)} style={s.inp} /></div>
                  <div style={s.mb}><label style={s.label}>Colour</label>
                    <input value={v(p, 'colour')} onChange={e => setEdit(p.id, 'colour', e.target.value)} style={s.inp} /></div>
                  <div style={s.mb}><label style={s.label}>Domestic Target Price (₹)</label>
                    <input type="number" value={v(p, 'domestic_srp')} onChange={e => setEdit(p.id, 'domestic_srp', e.target.value)} style={s.inp} /></div>
                  <div style={s.mb}><label style={s.label}>Domestic Buyer Price (₹)</label>
                    <input type="number" value={v(p, 'domestic_buyer_price')} onChange={e => setEdit(p.id, 'domestic_buyer_price', e.target.value)} style={s.inp} /></div>
                  <div style={{ ...s.stitle, marginTop: 14 }}>Weight</div>
                  <div style={s.mb}><label style={s.label}>Raw weight (kg)</label>
                    <input type="number" step="0.01" value={v(p, 'wt_raw')} onChange={e => setEdit(p.id, 'wt_raw', e.target.value)} style={s.inp} /></div>
                  <div style={s.mb}><label style={s.label}>Packed weight (kg) — from Amazon "Item Weight"</label>
                    <input type="number" step="0.01" value={v(p, 'wt_packed')} onChange={e => setEdit(p.id, 'wt_packed', e.target.value)} style={s.inp} /></div>
                  <div style={s.mb}><label style={s.label}>Chargeable weight (auto = packed × 1.3)</label>
                    <input value={p.wt_packed > 0 ? (p.wt_packed * 1.3).toFixed(3) + ' kg' : '—'} disabled style={{ ...s.inp, opacity: .5, cursor: 'not-allowed' }} /></div>
                </div>

                <div>
                  <div style={s.stitle}>Export costs</div>
                  <div style={s.mb}><label style={s.label}>Export Freight rate (₹ per kg)</label>
                    <input type="number" value={v(p, 'freight_rate')} onChange={e => setEdit(p.id, 'freight_rate', e.target.value)} style={s.inp} /></div>
                  <div style={s.mb}><label style={s.label}>Local Freight rate (₹ per kg)</label>
                    <input type="number" value={v(p, 'local_freight_rate')} onChange={e => setEdit(p.id, 'local_freight_rate', e.target.value)} style={s.inp} /></div>
                  <div style={s.mb}><label style={s.label}>Export Expenses (₹)</label>
                    <input type="number" value={v(p, 'other_export_cost')} onChange={e => setEdit(p.id, 'other_export_cost', e.target.value)} style={s.inp} /></div>
                  <div style={s.mb}><label style={s.label}>Buyer Price — selling price to buyer ($)</label>
                    <input type="number" step="0.01" value={v(p, 'srp_usd')} onChange={e => setEdit(p.id, 'srp_usd', e.target.value)} style={s.inp} /></div>
                </div>

                <div>
                  <div style={s.stitle}>Benefits</div>
                  <div style={s.brow}>
                    <span style={{ fontSize: 11, fontWeight: 500, width: 80, flexShrink: 0, color: 'var(--text2,#6b6960)' }}>Drawback</span>
                    <Tog on={bv(p, 'drawback_on')} onChange={val => saveToggle(p.id, { drawback_on: val })} />
                    <select value={bv(p, 'drawback_formula') || 'formula1'} disabled={!bv(p, 'drawback_on')}
                      onChange={e => saveToggle(p.id, { drawback_formula: e.target.value })}
                      style={{ ...s.inp, flex: 1, opacity: bv(p, 'drawback_on') ? 1 : .4 }}>
                      <option value="formula1">Formula 1 (4%×95%×ex-GST, cap ₹350)</option>
                      <option value="manual">Manual (₹)</option>
                    </select>
                    {bv(p, 'drawback_formula') === 'manual' && bv(p, 'drawback_on') && (
                      <input type="number" value={v(p, 'drawback_manual')} placeholder="₹"
                        onChange={e => setEdit(p.id, 'drawback_manual', e.target.value)} style={{ ...s.inp, width: 70 }} />
                    )}
                  </div>
                  <div style={s.brow}>
                    <span style={{ fontSize: 11, fontWeight: 500, width: 80, flexShrink: 0, color: 'var(--text2,#6b6960)' }}>RoDTEP</span>
                    <Tog on={bv(p, 'rodtep_on')} onChange={val => saveToggle(p.id, { rodtep_on: val })} />
                    <input type="number" value={v(p, 'rodtep_amt')} placeholder="₹ amount" disabled={!bv(p, 'rodtep_on')}
                      onChange={e => setEdit(p.id, 'rodtep_amt', e.target.value)}
                      style={{ ...s.inp, flex: 1, opacity: bv(p, 'rodtep_on') ? 1 : .4 }} />
                  </div>
                  {role === 'admin' && (<>
                    <div style={s.brow}>
                      <span style={{ fontSize: 11, fontWeight: 500, width: 80, flexShrink: 0, color: 'var(--text2,#6b6960)' }}>Aff CB</span>
                      <Tog on={bv(p, 'aff_cb_on')} onChange={val => saveToggle(p.id, { aff_cb_on: val })} />
                      <select value={bv(p, 'aff_cb_mode') || 'pct'} disabled={!bv(p, 'aff_cb_on')}
                        onChange={e => saveToggle(p.id, { aff_cb_mode: e.target.value })}
                        style={{ ...s.inp, width: 90, opacity: bv(p, 'aff_cb_on') ? 1 : .4 }}>
                        <option value="pct">% ex-GST</option>
                        <option value="abs">₹ fixed</option>
                      </select>
                      <input type="number"
                        value={bv(p, 'aff_cb_mode') === 'pct' ? v(p, 'aff_cb_pct') : v(p, 'aff_cb_amt')}
                        disabled={!bv(p, 'aff_cb_on')}
                        onChange={e => setEdit(p.id, bv(p, 'aff_cb_mode') === 'pct' ? 'aff_cb_pct' : 'aff_cb_amt', e.target.value)}
                        style={{ ...s.inp, flex: 1, opacity: bv(p, 'aff_cb_on') ? 1 : .4 }} />
                    </div>
                    <div style={s.brow}>
                      <span style={{ fontSize: 11, fontWeight: 500, width: 80, flexShrink: 0, color: 'var(--text2,#6b6960)' }}>Card CB</span>
                      <Tog on={bv(p, 'card_cb_on')} onChange={val => saveToggle(p.id, { card_cb_on: val })} />
                      <select value={bv(p, 'card_cb_mode') || 'pct'} disabled={!bv(p, 'card_cb_on')}
                        onChange={e => saveToggle(p.id, { card_cb_mode: e.target.value })}
                        style={{ ...s.inp, width: 90, opacity: bv(p, 'card_cb_on') ? 1 : .4 }}>
                        <option value="pct">% ex-GST</option>
                        <option value="abs">₹ fixed</option>
                      </select>
                      <input type="number"
                        value={bv(p, 'card_cb_mode') === 'pct' ? v(p, 'card_cb_pct') : v(p, 'card_cb_amt')}
                        disabled={!bv(p, 'card_cb_on')}
                        onChange={e => setEdit(p.id, bv(p, 'card_cb_mode') === 'pct' ? 'card_cb_pct' : 'card_cb_amt', e.target.value)}
                        style={{ ...s.inp, flex: 1, opacity: bv(p, 'card_cb_on') ? 1 : .4 }} />
                    </div>
                  </>)}
                </div>
              </div>

              <div style={{ display: 'flex', gap: 8, marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--border,#e2e0d8)' }}>
                <button onClick={() => saveProduct(p.id)}
                  style={{ padding: '8px 20px', background: '#1a1917', color: '#fff', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', opacity: saving[p.id] ? .7 : 1 }}>
                  {saving[p.id] ? 'Saving…' : 'Save changes'}
                </button>
                <button onClick={() => setConfirmDel({ id: p.id, name: p.name })}
                  style={{ padding: '8px 14px', border: '1px solid #fecaca', borderRadius: 6, fontSize: 12, color: '#dc2626', background: 'none', cursor: 'pointer' }}>
                  Remove product
                </button>
              </div>
              </>)}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
